import { useState, useEffect, useRef, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useSpotSwapPair, useLimitSwapPair } from '~/state/dex/hooks'
import { useUpdaterChainId } from '~/hooks'
import { useBreakpoint, useGA, useLimitOrderRouter } from '~/hooks'
import useSWR from 'swr'
import {
  getMarketPriceHistoryChartLine,
  getMarketPriceHistoryChartCandle,
} from '~/apis'
import TokenLogo from '../TokenLogo'
import { Row, RowBetween, FlexContainer } from '~/components/Layout'
import {
  SwapAreaChart,
  SwapCandleChart,
  SpreadLabelLite,
  usePriceSpread,
} from './components'
import { formatAmount } from '~/components/Exchange/helpers'
import { useOneOfModalOpened } from '~/state/application/hooks'
import { ApplicationModal } from '~/state/application/reducer'
import Lottie from 'lottie-react'
import animationData from './loadingchart.json'
import { zeroAddress } from 'viem'
import { WETH as WETH_ADDRESSES } from '~/constants/addresses'
import styled, { css } from 'styled-components'
import IconSwitch from '~/assets/images/switch-round-black.svg'
import IconCurveDefault from '~/assets/images/curve-icon-black.svg'
import IconCurveActive from '~/assets/images/curve-icon-purple.svg'
import IconCandleDefault from '~/assets/images/candle-icon-black.svg'
import IconCandleActive from '~/assets/images/candle-icon-purple.svg'
import { ReactComponent as IconMinimize } from '~/assets/images/minimize.svg'
import { ReactComponent as IconAttention } from '~/assets/images/attention-octagon.svg'
import iconETH from '~/assets/images/icon-eth.png'
import iconWETH from '~/assets/images/icon-weth.png'

interface SwapPriceChartProps {
  handleChartExpand?: () => void
}

enum Charts {
  Area = 'area',
  Candle = 'candle',
}

type DateItem = {
  label: string
  value: string
}

const AreaChartDateRange: Array<DateItem> = [
  { label: '1D', value: '1' },
  { label: '1W', value: '7' },
  { label: '1M', value: '30' },
  { label: '1Y', value: '365' },
]

const CandleChartDateRange: Array<DateItem> = [
  { label: '15M', value: '0.5' },
  { label: '1H', value: '2' },
  { label: '6H', value: '14' },
  { label: '1D', value: '90' },
]

const SwapPriceChart: React.FC<SwapPriceChartProps> = ({
  handleChartExpand,
}) => {
  const { isMobile } = useBreakpoint()
  const { t } = useTranslation()
  const { trackView } = useGA()
  const isLimitOrder = useLimitOrderRouter()

  const expectChainId = useUpdaterChainId()
  const [spotInputToken, spotOutputToken] = useSpotSwapPair()
  const [limitInputToken, limitOutputToken] = useLimitSwapPair()

  const inputToken = useMemo(
    () => (isLimitOrder ? limitInputToken : spotInputToken),
    [spotInputToken, limitInputToken, isLimitOrder],
  )
  const outputToken = useMemo(
    () => (isLimitOrder ? limitOutputToken : spotOutputToken),
    [spotOutputToken, limitOutputToken, isLimitOrder],
  )

  const wrapInputToken = {
    address: zeroAddress,
    decimals: 18,
    symbol: 'ETH',
    name: 'ETH',
    logo: iconETH.src,
  }
  const wrapOutputToken = {
    address: WETH_ADDRESSES[expectChainId],
    decimals: 18,
    symbol: 'WETH',
    name: 'WETH',
    logo: iconWETH.src,
  }
  const inputTokenRef = useRef(inputToken?.address)
  const outputTokenRef = useRef(outputToken?.address)
  const [leftToken, setLeftToken] = useState<
    SpotSwapToken | LimitSwapToken | BaseTokenlonToken
  >(inputToken)
  const [rightToken, setRightToken] = useState<
    SpotSwapToken | LimitSwapToken | BaseTokenlonToken
  >(outputToken)

  const [isInputDirection, setIsInputDirection] = useState<boolean>(true)
  const [selectedChartType, setSelectedChartType] = useState<Charts>(
    Charts.Area,
  )
  const [selectedDate, setSelectedDate] = useState<DateItem>(
    AreaChartDateRange[0],
  )

  const wethWrapOpened = useOneOfModalOpened([
    ApplicationModal.WETH_WRAP,
    ApplicationModal.WETH_PREVIEW,
    ApplicationModal.WETH_RESULT,
  ])

  const isAreaChart = selectedChartType === Charts.Area
  const isCandleChart = selectedChartType === Charts.Candle

  // 后端不同时间周期缓存时间不同，最小缓存时间为15min，与产品确认后前端定期重新请求间隔采用15min
  const { data: rawChartAreaData } = useSWR(
    isAreaChart
      ? [
          'getMarketPriceHistoryChartLine',
          selectedDate,
          leftToken?.symbol,
          rightToken?.symbol,
        ]
      : null,
    () =>
      getMarketPriceHistoryChartLine(
        selectedDate.value,
        leftToken?.symbol,
        rightToken?.symbol,
      ),
    { refreshInterval: 1000 * 60 * 15 },
  )

  const { data: rawChartCandleData } = useSWR(
    isCandleChart
      ? [
          'getMarketPriceHistoryChartCandle',
          selectedDate,
          leftToken?.symbol,
          rightToken?.symbol,
        ]
      : null,
    () =>
      getMarketPriceHistoryChartCandle(
        selectedDate.value,
        leftToken?.symbol,
        rightToken?.symbol,
      ),
    { refreshInterval: 1000 * 60 * 15 },
  )

  // rawChartAreaData 返回 null 时代表该币对暂未收录
  const chartAreaData = useMemo(
    () =>
      rawChartAreaData?.filter((item) => item.price !== 0) ?? rawChartAreaData,
    [rawChartAreaData],
  )

  const chartCandleData = useMemo(
    () =>
      rawChartCandleData?.filter(({ open, close, high, low }) => {
        return open !== 0 && close !== 0 && high !== 0 && low !== 0
      }) ?? rawChartCandleData,
    [rawChartCandleData],
  )

  const chartData = isAreaChart ? chartAreaData : chartCandleData

  const dateRange = isAreaChart ? AreaChartDateRange : CandleChartDateRange
  const iconCurve = isAreaChart ? IconCurveActive : IconCurveDefault
  const iconCandle = isCandleChart ? IconCandleActive : IconCandleDefault
  const rightTokenPrecision = Math.min(8, rightToken?.decimals ?? 8)

  const latestBasePrice = isAreaChart
    ? chartAreaData?.[0]?.price
    : chartCandleData?.[0]?.close
  const latestPrice = isAreaChart
    ? chartAreaData?.[chartAreaData.length - 1]?.price
    : chartCandleData?.[chartCandleData.length - 1]?.close

  const latestPriceLiteral = latestPrice
    ? formatAmount(latestPrice, rightTokenPrecision)
    : '~'

  const priceSpread = usePriceSpread(latestBasePrice, latestPrice)
  const isBelow = Number(priceSpread) < 0

  const dateFormatStr = useMemo(
    () =>
      Number(selectedDate.value) <= 2
        ? 'H:mm'
        : selectedDate.value === 'all'
        ? 'MMM YYYY'
        : 'MMM DD',
    [selectedDate],
  )

  const handleSelectChartType = (chart: Charts) => {
    setSelectedChartType(chart)
    const newDateRange =
      chart === Charts.Area ? AreaChartDateRange : CandleChartDateRange
    setSelectedDate(newDateRange[0])
  }

  const handleSelectDate = (date: DateItem) => {
    setSelectedDate(date)
  }

  const onSwitch = () => {
    setIsInputDirection(!isInputDirection)
  }

  useEffect(() => {
    trackView('webtl_products_quotes', {
      pair: `${leftToken?.symbol}/${rightToken?.symbol}`,
      pic_type: selectedChartType,
      time_type: selectedDate.label,
      source: isLimitOrder ? 'limit order' : 'instant',
    })
  }, [
    leftToken,
    rightToken,
    selectedChartType,
    selectedDate.label,
    isLimitOrder,
  ])

  useEffect(() => {
    if (wethWrapOpened) {
      inputTokenRef.current = wrapInputToken?.address
      outputTokenRef.current = wrapOutputToken?.address
    }
  }, [wethWrapOpened])

  useEffect(() => {
    const targetInputToken = wethWrapOpened ? wrapInputToken : inputToken
    const targetOutputToken = wethWrapOpened ? wrapOutputToken : outputToken

    setLeftToken(isInputDirection ? targetInputToken : targetOutputToken)
    setRightToken(isInputDirection ? targetOutputToken : targetInputToken)
  }, [
    inputToken?.address,
    outputToken?.address,
    isInputDirection,
    wrapInputToken?.address,
    wrapOutputToken?.address,
    wethWrapOpened,
  ])

  // change wrap eth form direction
  useEffect(() => {
    if (
      inputTokenRef.current === wrapOutputToken?.address &&
      outputTokenRef.current === wrapInputToken?.address
    ) {
      inputTokenRef.current = wrapInputToken?.address
      outputTokenRef.current = wrapOutputToken?.address
      setIsInputDirection(true)
    }
  }, [wrapInputToken?.address, wrapOutputToken?.address])

  // change spotswap or limitorder form direction
  useEffect(() => {
    if (
      inputTokenRef.current === outputToken?.address &&
      outputTokenRef.current === inputToken?.address
    ) {
      inputTokenRef.current = inputToken?.address
      outputTokenRef.current = outputToken?.address
      setIsInputDirection(true)
    }
  }, [inputToken?.address, outputToken?.address])

  return (
    <StyledChartContainer isLimitOrder={isLimitOrder}>
      <Row>
        <TokenLogo
          space={0}
          logo={leftToken?.logo}
          symbol={rightToken?.symbol}
        />
        <StyledOutputTokenWrapper>
          <TokenLogo
            space={0}
            logo={rightToken?.logo}
            symbol={rightToken?.symbol}
          />
        </StyledOutputTokenWrapper>
        <StyledTokenInfo>
          {leftToken?.symbol}/{rightToken?.symbol}
        </StyledTokenInfo>
        <SwitchIcon src={IconSwitch} onClick={onSwitch} />
      </Row>
      <RowBetween margin="13px 0 0" wrap="wrap">
        <FlexContainer gap={6} align="center">
          <StyledValue>{`${latestPriceLiteral} ${rightToken?.symbol}`}</StyledValue>
          <SpreadLabelLite
            latestBasePrice={latestBasePrice}
            latestPrice={latestPrice}
          />
        </FlexContainer>
        {isMobile && <Divider />}
        <StyledSettingsWrapper>
          <FlexContainer gap={8} align="center">
            {dateRange.map((item, index) => (
              <StyledSettingButton
                active={selectedDate.value === item.value}
                key={index}
                onClick={() => handleSelectDate(item)}
              >
                {item.label}
              </StyledSettingButton>
            ))}
          </FlexContainer>
          <StyledPipe />
          <FlexContainer gap={8} align="center">
            <StyledSettingsIcon
              active={isAreaChart}
              src={iconCurve}
              onClick={() => handleSelectChartType(Charts.Area)}
            />
            <StyledSettingsIcon
              active={isCandleChart}
              src={iconCandle}
              onClick={() => handleSelectChartType(Charts.Candle)}
            />
          </FlexContainer>
        </StyledSettingsWrapper>
      </RowBetween>
      {!isMobile && <Divider />}

      {!chartData &&
        (chartData === null ? (
          <StyledPlaceholder
            direction="column"
            justify="center"
            align="center"
            gap={8}
          >
            <IconAttention />
            <p>{t('swap_chart_placeholder')}</p>
          </StyledPlaceholder>
        ) : (
          <StyledLoadingContainer>
            <Lottie
              animationData={animationData}
              loop
              style={{ width: '100%' }}
            />
          </StyledLoadingContainer>
        ))}
      {chartData &&
        (isAreaChart ? (
          <SwapAreaChart
            data={chartData}
            isBelow={isBelow}
            dateFormatStr={dateFormatStr}
          />
        ) : (
          <SwapCandleChart data={chartData} dateFormatStr={dateFormatStr} />
        ))}
      {isMobile && (
        <StyledIconExpandWrapper onClick={handleChartExpand}>
          <IconMinimize />
        </StyledIconExpandWrapper>
      )}
    </StyledChartContainer>
  )
}

export default SwapPriceChart

const StyledChartContainer = styled.div<{
  isLimitOrder: boolean
}>`
  position: relative;
  flex: auto;
  display: flex;
  flex-direction: column;
  width: 369px;
  min-height: 480px;
  background: #ffffff;
  border: 0.5px solid #e7e2ff;
  border-radius: 16px;
  padding: 15px 20px 20px;
  margin: 68px 0 12px;

  ${({ theme }) => theme.media.sm`
    width: calc(100% - 30px);
    max-width: 435px;
    order: 1;
    margin: ${({ isLimitOrder }) =>
      isLimitOrder
        ? '0 15px 15px'
        : '0 15px calc(45px + env(safe-area-inset-bottom))'};
    padding: 15px 10px 12px;    
  `}
`

const StyledOutputTokenWrapper = styled.span`
  margin-left: -6px;
`

const StyledTokenInfo = styled.span`
  margin-left: 7px;
  margin-right: 4px;
  font-weight: 500;
  font-size: 15px;
  line-height: 20px;
  color: #2c2842;
`

const SwitchIcon = styled.img`
  width: 16px;
  height: 16px;
  cursor: pointer;
`

const StyledValue = styled.span`
  font-weight: 700;
  font-size: 20px;
  line-height: 20px;
  color: #2c2842;
`

const StyledSettingsWrapper = styled.div`
  display: flex;
  align-items: center;
`

const StyledSettingButton = styled.span<{ active: boolean }>`
  width: 33px;
  height: 28px;
  padding: 5px 0;
  border-radius: 8px;

  font-weight: 500;
  font-size: 13px;
  line-height: 140%;
  text-align: center;
  color: #2c2842;
  cursor: pointer;

  ${({ active }) =>
    active &&
    css`
      background: #eff0fa;
      color: #604dbc;
    `}
`

const StyledSettingsIcon = styled.img<{ active: boolean }>`
  width: 28px;
  height: 28px;
  border-radius: 8px;
  cursor: pointer;

  ${({ active }) =>
    active &&
    css`
      background: #eff0fa;
    `}
`

const Divider = styled.div`
  border-bottom: 1px solid rgb(239, 240, 250);
  width: 100%;
  margin: 13px 0;
`

const StyledPipe = styled.div`
  background: #9598ab;
  height: 16px;
  margin: 0 16px;
  width: 1px;
`

const StyledIconExpandWrapper = styled.div`
  position: absolute;
  top: 8px;
  right: 8px;
  display: flex;
  justify-content: flex-end;
  width: 40px;
  height: 40px;
  cursor: pointer;
`

const StyledLoadingContainer = styled.div`
  flex: auto;
  display: flex;
  justify-content: center;
  align-items: center;
  width: 100%;
`

const StyledPlaceholder = styled(FlexContainer)`
  flex: auto;
  width: 100%;
  border-radius: 16px;
  background: linear-gradient(
    360deg,
    #f5f7fb 1.75%,
    rgba(250, 250, 253, 0) 100%
  );

  font-weight: 400;
  font-size: 14px;
  line-height: 140%;
  color: #6f7390;
`
